import { parseUsd } from "./billing-money";
import type { BillingSignedDocument } from "./billing-document-schemas";
import type { BillingLineInput, BillingTermsInput, BillingTool } from "./billing-schemas";
import { COMMERCIAL_DOCUMENT_LABELS } from "./commercial-documents";
import { commercialTermCategory, type CommercialTerm } from "./commercial-terms";

/**
 * A signed order form's commercial terms, as the billing terms they become (ADR-0073). Fees become
 * one-time lines, one-time credits come off them as the terms discount, and subscriptions become
 * recurring lines. Anything else (a discount for a period, payment terms, a price that is not a
 * plain amount) is kept in the notes and listed in `unread`, so someone checks it by hand.
 */

const DAY = 86_400;

export type DocumentTerms = {
  terms: Omit<BillingTermsInput, "reason" | "supersedesTermsId">;
  /** What the document says that billing could not carry over, as written. */
  unread: string[];
};

function describe(term: CommercialTerm): string {
  return `${term.label}: ${term.value}${term.duration ? ` (${term.duration})` : ""}`;
}

type Collected = { lines: BillingLineInput[]; credit: number; unread: string[] };

function collect(term: CommercialTerm, into: Collected): void {
  const category = commercialTermCategory(term);
  const amount = parseUsd(term.value);
  if (amount === null || amount === 0 || category === "terms") {
    into.unread.push(describe(term));
    return;
  }
  if (term.discount) {
    // A one-time credit comes off the fees; a subscription discount for a period has no billing
    // equivalent yet, so it waits in the notes.
    if (category === "fixed") into.credit += amount;
    else into.unread.push(describe(term));
    return;
  }
  into.lines.push({
    kind: category === "fixed" ? "one_time" : "recurring",
    description: term.label.slice(0, 200),
    unitAmountMinor: amount,
    quantity: 1,
    cadence: category === "fixed" ? null : category === "annual" ? "year" : "month",
    discount: null,
  });
}

export function termsFromDocument(
  document: BillingSignedDocument,
  tools: readonly BillingTool[],
): DocumentTerms {
  const collected: Collected = { lines: [], credit: 0, unread: [] };
  for (const term of document.commercialTerms) collect(term, collected);
  const fees = collected.lines
    .filter((line) => line.kind === "one_time")
    .reduce((total, line) => total + line.unitAmountMinor, 0);
  const credit = Math.min(collected.credit, fees);
  if (collected.credit > credit) collected.unread.push("A credit larger than the one-time fees");
  const label = COMMERCIAL_DOCUMENT_LABELS[document.kind].toLowerCase();
  const notes = [
    `From the signed ${label} ${document.reference}, signed ${document.signedOn}.`,
    ...(collected.unread.length
      ? ["Not carried over; check these by hand:", ...collected.unread]
      : []),
  ].join("\n");
  const toolName = document.toolName.trim().toLowerCase();
  return {
    terms: {
      toolId: tools.find((tool) => tool.displayName.trim().toLowerCase() === toolName)?.id ?? null,
      title: (document.toolName.trim() || document.title).slice(0, 160),
      effectiveAt: Math.floor(Date.parse(`${document.signedOn}T00:00:00Z`) / 1_000 / DAY) * DAY,
      endsAt: null,
      notes: notes.slice(0, 4_000),
      discount: credit > 0 ? { type: "fixed", amountMinor: credit } : null,
      lines: collected.lines,
    },
    unread: collected.unread,
  };
}
